import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import ProjectCategoryService from "../../services/ProjectCategoryService";
import ProjectService from "../../services/ProjectService";
import ProjectLocationService from "../../services/ProjectLocationService";
import ItemCategoryService from "../../services/ItemCategoryService";
import ItemService from "../../services/ItemsServices";
import MaterialConsumptionReportService from "../../services/materialConsumptionReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../assets/images/deviops_logo.png';
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";

export default function MaterialConsumptionReport() {
    const activeCompany = StorageService.activeCompany();
    const companyId = activeCompany?.companyId || "";
    const loginData = StorageService.loginData();
    const employeeId = loginData?.employeeId || "";
    const roleId = loginData?.roleId || "";

    const [projectCategoryId, setProjectCategoryId] = useState("");
    const [projectId, setProjectId] = useState("");
    const [locationId, setLocationId] = useState("");
    const [itemCategoryId, setItemCategoryId] = useState("");
    const [itemCode, setItemCode] = useState("");

    const [projectCategories, setProjectCategories] = useState([]);
    const [projects, setProjects] = useState([]);
    const [locations, setLocations] = useState([]);
    const [itemCategories, setItemCategories] = useState([]);
    const [items, setItems] = useState([]);

    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [msgType, setMsgType] = useState("");

    // ✅ PAGINATION STATES
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    const selectedCategory = projectCategories.find(
        (cat) => String(cat.id) === String(projectCategoryId)
    );
    const selectedCategoryCode = selectedCategory?.categoryCode;

    const filteredProjects = projectCategoryId
        ? projects.filter((project) => {
            if (project.projectCategoryId) {
                return String(project.projectCategoryId) === String(projectCategoryId);
            }
            return project.projectCategoryCode === selectedCategoryCode;
        })
        : projects;

    const showMessage = (text, type = "error") => {
        setMessage(text);
        setMsgType(type);
    };

    const loadDropdowns = async () => {
        try {
            const user = StorageService.loginData();
            let projectRes;

            if (Number(user.roleId) === 1 || Number(user.roleId) === 2) {
                projectRes = await ProjectService.getActive(companyId);
            } else {
                projectRes = await ProjectService.getAssignedProjects(companyId, user.employeeId);
            }

            const [projectCategoryRes, itemCategoryRes] = await Promise.all([
                ProjectCategoryService.getAll(),
                ItemCategoryService.getActive()
            ]);

            setProjectCategories(projectCategoryRes?.data || projectCategoryRes || []);
            setItemCategories(itemCategoryRes?.data || itemCategoryRes || []);

            let projectList = [];
            if (Number(user.roleId) === 1 || Number(user.roleId) === 2) {
                projectList = Array.isArray(projectRes) ? projectRes : (projectRes?.data || []);
            } else {
                projectList = projectRes?.data?.data || [];
            }
            setProjects(projectList);

        } catch (error) {
            console.error(error);
            showMessage("Failed to load filter dropdowns");
        }
    };

    const loadLocationsByProject = async (projId) => {
        if (!projId) {
            setLocations([]);
            return;
        }
        try {
            const res = await ProjectLocationService.getByProjectId(projId);
            setLocations(res?.data || res || []);
        } catch (err) {
            console.error("Location load error", err);
            setLocations([]);
        }
    };

    const loadItemsByCategory = async (catId) => {
        if (!catId) {
            setItems([]);
            return;
        }
        try {
            const res = await ItemService.getItemByCategoryId(catId);
            const list = Array.isArray(res) ? res : (res?.data || []);
            setItems(list);
        } catch (err) {
            console.error("Items load error", err);
            setItems([]);
        }
    };

    const loadReport = async (pageNumber = page) => {
        setLoading(true);
        try {
            const res = await MaterialConsumptionReportService.getReport({
                companyId,
                employeeId,
                roleId,
                projectCategoryId: projectCategoryId || undefined,
                projectId: projectId || undefined,
                locationId: locationId || undefined,
                itemCategoryId: itemCategoryId || undefined,
                itemCode: itemCode || undefined,
                page: pageNumber,
                limit
            });

            setReports(res?.data || []);
            setTotalPages(Number(res?.pagination?.totalPages) || 0);
            setTotalRecords(Number(res?.pagination?.total) || 0);
            setPage(pageNumber);

        } catch (error) {
            console.error(error);
            setReports([]);
            showMessage("Error fetching material consumption report");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) {
            loadDropdowns();
            loadReport(1);
        }
    }, [companyId]);

    // Reset pagination to first page when filters change
    useEffect(() => {
        setPage(1);
    }, [projectCategoryId, projectId, locationId, itemCategoryId, itemCode]);

    const handleClearFilters = () => {
        setProjectCategoryId("");
        setProjectId("");
        setLocationId("");
        setItemCategoryId("");
        setItemCode("");
        setLocations([]);
        setItems([]);
        setPage(1);
        setTotalPages(0);
        setTotalRecords(0);

        // Load report with cleared filters
        setTimeout(() => {
            loadReport(1);
        }, 100);
    };

    const exportExcel = async () => {
        if (!projectCategoryId) {
            showMessage("Project Category is mandatory to export Excel");
            return;
        }
        if (!projectId) {
            showMessage("Project Name is mandatory to export Excel");
            return;
        }
        try {
            const allData = await MaterialConsumptionReportService.exportExcel({
                companyId,
                employeeId,
                roleId,
                projectCategoryId: projectCategoryId || undefined,
                projectId: projectId || undefined,
                locationId: locationId || undefined,
                itemCategoryId: itemCategoryId || undefined,
                itemCode: itemCode || undefined
            });

            if (!allData.length) {
                showMessage("No data to export");
                return;
            }

            const wb = XLSX.utils.book_new();
            const ws = {};
            ws["!merges"] = [];

            // FILTER LABELS
            let categoryLabel = "All";
            if (projectCategoryId) {
                const found = projectCategories.find(c => String(c.id) === String(projectCategoryId));
                categoryLabel = found?.categoryName || projectCategoryId;
            }

            let projectLabel = "All";
            if (projectId) {
                const found = projects.find(p => String(p.id || p.projectId) === String(projectId));
                projectLabel = found?.projectName || projectId;
            }

            let locationLabel = "All";
            if (locationId) {
                const found = locations.find(l => String(l.id) === String(locationId));
                locationLabel = found?.locationName || locationId;
            }

            let itemCategoryLabel = "All";
            if (itemCategoryId) {
                const found = itemCategories.find(ic => String(ic.id) === String(itemCategoryId));
                itemCategoryLabel = found?.itemCategoryName || itemCategoryId;
            }

            let itemLabel = "All";
            if (itemCode) {
                const found = items.find(i => String(i.itemCode) === String(itemCode));
                itemLabel = found?.itemName || itemCode;
            }

            let row = 0;
            const companyName = activeCompany?.companyName || "";

            // STYLES
            const thin = { style: "thin" };
            const center = { horizontal: "center", vertical: "center", wrapText: true };
            const border = { top: thin, bottom: thin, left: thin, right: thin };

            // COMPANY NAME (Top Left)
            XLSX.utils.sheet_add_aoa(ws, [[companyName]], { origin: { r: row, c: 0 } });
            ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
                font: { bold: true, sz: 12 }
            };

            // REPORT DATE (Top Right)
            XLSX.utils.sheet_add_aoa(
                ws,
                [[`Report Date: ${utility.formatExportDateTime(new Date())}`]],
                { origin: { r: row, c: 8 } }
            );
            ws[XLSX.utils.encode_cell({ r: row, c: 8 })].s = {
                alignment: { horizontal: "right" },
                font: { bold: true, sz: 11 }
            };

            row++;

            // TITLE
            XLSX.utils.sheet_add_aoa(ws, [["Material Consumption Report"]], { origin: { r: row, c: 0 } });
            ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 8 } });
            for (let c = 0; c <= 8; c++) {
                const titleCell = XLSX.utils.encode_cell({ r: row, c });
                if (!ws[titleCell]) ws[titleCell] = { t: "s", v: "" };
                ws[titleCell].s = {
                    font: { bold: true, sz: 16 },
                    alignment: center
                };
            }
            row += 2;

            // FILTER INFO
            XLSX.utils.sheet_add_aoa(ws, [
                ["Project Category:", categoryLabel, "Project:", projectLabel, "Location:", locationLabel],
                ["Item Category:", itemCategoryLabel, "Item:", itemLabel, "", ""]
            ], { origin: { r: row, c: 0 } });

            const filterLabelStyle = {
                font: { bold: true, sz: 10 },
                alignment: { horizontal: "left", vertical: "center" },
                border: border
            };
            const filterValueStyle = {
                font: { sz: 10 },
                alignment: { horizontal: "left", vertical: "center" },
                border: border
            };

            const boldColumns = [0, 2, 4];
            for (let i = 0; i < 2; i++) {
                for (let c = 0; c < 6; c++) {
                    const cell = XLSX.utils.encode_cell({ r: row + i, c });
                    if (!ws[cell]) continue;
                    ws[cell].s = boldColumns.includes(c) ? filterLabelStyle : filterValueStyle;
                }
            }
            row += 3;

            // TABLE HEADERS
            const headers = [
                "S.no",
                "Item Category",
                "Item Name",
                "Issued Qty",
                "Consumed Qty",
                "Lost Qty",
                "Damaged Qty",
                "Returned Qty",
                "Site Balance"
            ];
            const headerRowIndex = row;
            XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });

            headers.forEach((_, colIndex) => {
                const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
                ws[cell].s = {
                    font: { bold: true, sz: 11 },
                    alignment: center,
                    border: border
                };
            });
            row++;

            // TABLE DATA
            allData.forEach((reportRow, index) => {
                const rowData = [
                    index + 1,
                    reportRow.itemCategoryName || "-",
                    reportRow.itemCode || "-",
                    Number(reportRow.issuedQty || 0),
                    Number(reportRow.consumedQty || 0),
                    Number(reportRow.lostQty || 0),
                    Number(reportRow.damagedQty || 0),
                    Number(reportRow.returnedQty || 0),
                    Number(reportRow.siteBalance || 0)
                ];

                XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });

                rowData.forEach((_, colIndex) => {
                    const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
                    const isQtyNumber = colIndex >= 3;
                    const isSNo = colIndex === 0;
                    ws[cell].s = {
                        alignment: {
                            horizontal: isSNo ? "center" : (isQtyNumber ? "right" : "left"),
                            vertical: "center"
                        },
                        border: border
                    };
                    if (isSNo) {
                        ws[cell].t = 'n';
                        ws[cell].z = '0';
                    } else if (isQtyNumber) {
                        ws[cell].t = 'n';
                        ws[cell].z = '0.000';
                    }
                });
                row++;
            });

            // TOTAL ROW
            const totalIssued = allData.reduce((sum, r) => sum + Number(r.issuedQty || 0), 0);
            const totalConsumed = allData.reduce((sum, r) => sum + Number(r.consumedQty || 0), 0);
            const totalLost = allData.reduce((sum, r) => sum + Number(r.lostQty || 0), 0);
            const totalDamaged = allData.reduce((sum, r) => sum + Number(r.damagedQty || 0), 0);
            const totalReturned = allData.reduce((sum, r) => sum + Number(r.returnedQty || 0), 0);
            const totalSiteBalance = allData.reduce((sum, r) => sum + Number(r.siteBalance || 0), 0);

            ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });

            const totals = [
                "Total(s)",
                "",
                "",
                totalIssued,
                totalConsumed,
                totalLost,
                totalDamaged,
                totalReturned,
                totalSiteBalance
            ];

            XLSX.utils.sheet_add_aoa(ws, [totals], { origin: { r: row, c: 0 } });

            totals.forEach((_, colIndex) => {
                const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
                if (!ws[cell]) return;
                const isNumber = colIndex >= 3;
                ws[cell].s = {
                    font: { bold: true },
                    alignment: {
                        horizontal: isNumber || colIndex === 0 ? "right" : "center",
                        vertical: "center"
                    },
                    border: border
                };
                if (isNumber) {
                    ws[cell].t = 'n';
                    ws[cell].z = '#,##0.000';
                }
            });

            ws["!autofilter"] = {
                ref: XLSX.utils.encode_range({
                    s: { r: headerRowIndex, c: 0 },
                    e: { r: row - 1, c: headers.length - 1 }
                })
            };

            // Column Widths
            ws["!cols"] = [
                { wch: 8 },
                { wch: 30 },
                { wch: 40 },
                { wch: 15 },
                { wch: 15 },
                { wch: 12 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 }
            ];

            XLSX.utils.book_append_sheet(wb, ws, "MaterialConsumption");
            XLSX.writeFile(wb, "Material_Consumption_Report.xlsx");

        } catch (error) {
            console.error(error);
            showMessage("Error exporting Excel");
        }
    };

    const exportPdf = async () => {
        if (!projectCategoryId) {
            showMessage("Project Category is mandatory to export PDF");
            return;
        }
        if (!projectId) {
            showMessage("Project Name is mandatory to export PDF");
            return;
        }
        try {
            const allData = await MaterialConsumptionReportService.exportExcel({
                companyId,
                employeeId,
                roleId,
                projectCategoryId: projectCategoryId || undefined,
                projectId: projectId || undefined,
                locationId: locationId || undefined,
                itemCategoryId: itemCategoryId || undefined,
                itemCode: itemCode || undefined
            });

            if (!allData.length) {
                showMessage("No data to export");
                return;
            }

            const doc = new jsPDF("landscape");
            const pdfLogo = await getPdfLogoDataUrl(logo);
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const pageMargin = 14;
            const right = pageWidth - pageMargin;
            const left = pageMargin;
            const companyName = activeCompany?.companyName || "Devi Projects";
            const title = "Material Consumption Report";

            const drawHeader = (pageNumber) => {
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.2);

                doc.setFont(undefined, "bold");
                doc.setFontSize(12);
                const companyNameWidth = doc.getTextWidth(companyName);
                const logoWidth = 5;
                const logoHeight = 10;
                const logoX = left + 6 + (companyNameWidth - logoWidth) / 2;
                const logoY = 8;

                doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
                doc.text(companyName, left + 6, 23);

                doc.setFontSize(10);
                doc.setFont(undefined, "normal");
                doc.text(`Report Date: ${utility.formatExportDateTime(new Date())}`, right, 23, { align: "right" });

                doc.setFontSize(18);
                doc.setFont(undefined, "bold");
                doc.text(title, pageWidth / 2, 35, { align: "center" });

                const titleWidth = doc.getTextWidth(title);
                doc.setLineWidth(0.3);
                doc.line(pageWidth / 2 - titleWidth / 2, 37, pageWidth / 2 + titleWidth / 2, 37);
            };

            drawHeader(1);
            let yPosition = 45;

            // Filter Labels
            let categoryLabel = "All";
            if (projectCategoryId) {
                const found = projectCategories.find(c => String(c.id) === String(projectCategoryId));
                categoryLabel = found?.categoryName || projectCategoryId;
            }

            let projectLabel = "All";
            if (projectId) {
                const found = projects.find(p => String(p.id || p.projectId) === String(projectId));
                projectLabel = found?.projectName || projectId;
            }

            let locationLabel = "All";
            if (locationId) {
                const found = locations.find(l => String(l.id) === String(locationId));
                locationLabel = found?.locationName || locationId;
            }

            let itemCategoryLabel = "All";
            if (itemCategoryId) {
                const found = itemCategories.find(ic => String(ic.id) === String(itemCategoryId));
                itemCategoryLabel = found?.itemCategoryName || itemCategoryId;
            }

            let itemLabel = "All";
            if (itemCode) {
                const found = items.find(i => String(i.itemCode) === String(itemCode));
                itemLabel = found?.itemName || itemCode;
            }

            doc.setFontSize(9);
            doc.setFont(undefined, "bold");

            let xLeft = 14;
            let xMid = 110;
            let xRight = 200;
            let yHeader = yPosition;

            doc.text("Project Category:", xLeft, yHeader);
            doc.setFont(undefined, "normal");
            doc.text(String(categoryLabel), xLeft + 35, yHeader);

            doc.setFont(undefined, "bold");
            doc.text("Project Name:", xLeft, yHeader + 5);
            doc.setFont(undefined, "normal");
            doc.text(String(projectLabel), xLeft + 35, yHeader + 5);

            doc.setFont(undefined, "bold");
            doc.text("Location Name:", xMid, yHeader);
            doc.setFont(undefined, "normal");
            doc.text(String(locationLabel), xMid + 35, yHeader);

            doc.setFont(undefined, "bold");
            doc.text("Item Category:", xMid, yHeader + 5);
            doc.setFont(undefined, "normal");
            doc.text(String(itemCategoryLabel), xMid + 35, yHeader + 5);

            doc.setFont(undefined, "bold");
            doc.text("Item:", xRight, yHeader);
            doc.setFont(undefined, "normal");
            doc.text(String(itemLabel), xRight + 20, yHeader);

            yPosition += 15;

            const body = allData.map((row, index) => [
                index + 1,
                row.itemCategoryName || "-",
                row.itemCode || "-",
                Number(row.issuedQty || 0).toFixed(3),
                Number(row.consumedQty || 0).toFixed(3),
                Number(row.lostQty || 0).toFixed(3),
                Number(row.damagedQty || 0).toFixed(3),
                Number(row.returnedQty || 0).toFixed(3),
                Number(row.siteBalance || 0).toFixed(3)
            ]);

            const totalIssued = allData.reduce((sum, r) => sum + Number(r.issuedQty || 0), 0);
            const totalConsumed = allData.reduce((sum, r) => sum + Number(r.consumedQty || 0), 0);
            const totalLost = allData.reduce((sum, r) => sum + Number(r.lostQty || 0), 0);
            const totalDamaged = allData.reduce((sum, r) => sum + Number(r.damagedQty || 0), 0);
            const totalReturned = allData.reduce((sum, r) => sum + Number(r.returnedQty || 0), 0);
            const totalSiteBalance = allData.reduce((sum, r) => sum + Number(r.siteBalance || 0), 0);

            autoTable(doc, {
                startY: yPosition,
                head: [[
                    "S.no",
                    "Item Category",
                    "Item Name",
                    "Issued Qty",
                    "Consumed Qty",
                    "Lost Qty",
                    "Damaged Qty",
                    "Returned Qty",
                    "Site Balance"
                ]],
                body,
                foot: [[
                    { content: "Total(s)", colSpan: 3, styles: { halign: "right" } },
                    totalIssued.toFixed(3),
                    totalConsumed.toFixed(3),
                    totalLost.toFixed(3),
                    totalDamaged.toFixed(3),
                    totalReturned.toFixed(3),
                    totalSiteBalance.toFixed(3)
                ]],
                showFoot: "lastPage",
                theme: "grid",
                styles: {
                    fontSize: 8,
                    lineColor: [0, 0, 0],
                    lineWidth: 0.15,
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0]
                },
                alternateRowStyles: {
                    fillColor: [255, 255, 255]
                },
                headStyles: {
                    fillColor: [230, 230, 230],
                    textColor: [0, 0, 0],
                    fontStyle: "bold",
                    halign: "center"
                },
                columnStyles: {
                    0: { halign: "right" },
                    1: { halign: "left" },
                    2: { halign: "left" },
                    3: { halign: "right" },
                    4: { halign: "right" },
                    5: { halign: "right" },
                    6: { halign: "right" },
                    7: { halign: "right" },
                    8: { halign: "right" }
                },
                footStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    fontStyle: "bold",
                    halign: "right"
                },
                didParseCell: function (data) {
                    if (data.section === "foot") {
                        data.cell.styles.halign = "right";
                    }
                },
                margin: {
                    top: 40,
                    left: 14,
                    right: 14
                },
                didDrawPage: function (data) {
                    if (data.pageNumber === 1) return;
                    drawHeader(data.pageNumber);
                    doc.setFontSize(9);
                }
            });

            const pageCount = doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(9);
                doc.setFont(undefined, "normal");
                doc.text(
                    `Page ${i} of ${pageCount}`,
                    pageWidth / 2,
                    pageHeight - 10,
                    { align: "center" }
                );

                if (i === pageCount) {
                    doc.setFontSize(9);
                    doc.text("Prepared By", 20, pageHeight - 22);
                    doc.text("Checked By", pageWidth / 2, pageHeight - 22, { align: "center" });
                    doc.text("Approved By", pageWidth - 20, pageHeight - 22, { align: "right" });
                }
            }

            doc.save("MaterialConsumptionReport.pdf");

        } catch (error) {
            console.error(error);
            showMessage("Error exporting PDF");
        }
    };

    const getFormatValue = (val) => {
        const num = Number(val);
        return isNaN(num) ? "0.000" : num.toFixed(3);
    };

    // Calculate totals for currently displayed reports page
    const pageIssuedTotal = reports.reduce((sum, r) => sum + Number(r.issuedQty || 0), 0);
    const pageConsumedTotal = reports.reduce((sum, r) => sum + Number(r.consumedQty || 0), 0);
    const pageLostTotal = reports.reduce((sum, r) => sum + Number(r.lostQty || 0), 0);
    const pageDamagedTotal = reports.reduce((sum, r) => sum + Number(r.damagedQty || 0), 0);
    const pageReturnedTotal = reports.reduce((sum, r) => sum + Number(r.returnedQty || 0), 0);
    const pageSiteBalanceTotal = reports.reduce((sum, r) => sum + Number(r.siteBalance || 0), 0);

    return (
        <>
            <Breadcrumb pageTitle="Material Consumption Report" secondTitle="Reports" />

            <MessageDialog
                message={message}
                type={msgType}
                setMessage={setMessage}
                setMsgType={setMsgType}
            />

            <div className="card p-4 bg-white shadow-sm">
                <div className="row g-3 mb-3">
                    <div className="col-md-3">
                        <label className="form-label">
                            Project Category <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-select"
                            value={projectCategoryId}
                            onChange={(e) => {
                                setProjectCategoryId(e.target.value);
                                setProjectId("");
                                setLocationId("");
                            }}
                        >
                            <option value="">All Categories</option>
                            {projectCategories.map((category) => (
                                <option key={category.id} value={category.id}>
                                    {category.categoryName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label">
                            Project Name <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-select"
                            value={projectId}
                            onChange={(e) => {
                                const selectedProjectId = e.target.value;
                                setProjectId(selectedProjectId);
                                setLocationId("");
                                loadLocationsByProject(selectedProjectId);
                            }}
                        >
                            <option value="">All Projects</option>
                            {filteredProjects.map((project) => (
                                <option key={project.id || project.projectId} value={project.id || project.projectId}>
                                    {project.projectName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label">Location</label>
                        <select
                            className="form-select"
                            value={locationId}
                            onChange={(e) => setLocationId(e.target.value)}
                            disabled={!projectId}
                        >
                            <option value="">All Locations</option>
                            {locations.map((loc) => (
                                <option key={loc.id || loc.locationId} value={loc.id || loc.locationId}>
                                    {loc.locationName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label">Item Category</label>
                        <select
                            className="form-select"
                            value={itemCategoryId}
                            onChange={(e) => {
                                const selectedCatId = e.target.value;
                                setItemCategoryId(selectedCatId);
                                setItemCode("");
                                loadItemsByCategory(selectedCatId);
                            }}
                        >
                            <option value="">All Categories</option>
                            {itemCategories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.itemCategoryName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label">Item</label>
                        <select
                            className="form-select"
                            value={itemCode}
                            onChange={(e) => setItemCode(e.target.value)}
                            disabled={!itemCategoryId}
                        >
                            <option value="">All Items</option>
                            {items.map((it) => (
                                <option key={it.id} value={it.itemCode}>
                                    {it.itemCode}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="mb-3 d-flex justify-content-end align-items-center">
                    <div className="d-flex gap-2">
                        <button className="btn btn-secondary" onClick={handleClearFilters}>
                            <i className="ti ti-refresh me-1"></i>
                            Clear
                        </button>
                        <button className="btn btn-primary" onClick={() => loadReport(1)} disabled={loading}>
                            <i className="ti ti-search me-1"></i>
                            {loading ? "Searching..." : "View Report"}
                        </button>
                    </div>
                </div>

                <hr />

                <div className="mb-3 d-flex justify-content-end align-items-center">
                    <div className="d-flex gap-2">
                        <button className="btn btn-success" onClick={exportPdf} disabled={loading}>
                            <i className="ti ti-file-type-pdf me-1"></i>
                            Export PDF
                        </button>
                        <button className="btn btn-success" onClick={exportExcel} disabled={loading}>
                            <i className="ti ti-file-spreadsheet me-1"></i>
                            Export Excel
                        </button>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-bordered table-striped mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>S.no</th>
                                <th>Item Category</th>
                                <th>Item Name</th>
                                <th className="text-end">Issued Qty</th>
                                <th className="text-end">Consumed Qty</th>
                                <th className="text-end">Lost Qty</th>
                                <th className="text-end">Damaged Qty</th>
                                <th className="text-end">Returned Qty</th>
                                <th className="text-end">Site Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" className="text-center">
                                        <div className="spinner-border text-primary" />
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="text-center">
                                        No data found
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {reports.map((row, index) => (
                                        <tr key={row.itemCode || index}>
                                            <td>{(page - 1) * limit + index + 1}</td>
                                            <td>{row.itemCategoryName || "-"}</td>
                                            <td>{row.itemCode || "-"}</td>
                                            <td className="text-end">{getFormatValue(row.issuedQty)}</td>
                                            <td className="text-end">{getFormatValue(row.consumedQty)}</td>
                                            <td className="text-end">{getFormatValue(row.lostQty)}</td>
                                            <td className="text-end">{getFormatValue(row.damagedQty)}</td>
                                            <td className="text-end">{getFormatValue(row.returnedQty)}</td>
                                            <td className="text-end">{getFormatValue(row.siteBalance)}</td>
                                        </tr>
                                    ))}
                                    <tr className="fw-bold table-light">
                                        <td colSpan="3" className="text-end">Total(s)</td>
                                        <td className="text-end">{getFormatValue(pageIssuedTotal)}</td>
                                        <td className="text-end">{getFormatValue(pageConsumedTotal)}</td>
                                        <td className="text-end">{getFormatValue(pageLostTotal)}</td>
                                        <td className="text-end">{getFormatValue(pageDamagedTotal)}</td>
                                        <td className="text-end">{getFormatValue(pageReturnedTotal)}</td>
                                        <td className="text-end">{getFormatValue(pageSiteBalanceTotal)}</td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div>
                                Total Records: <strong>{totalRecords}</strong>
                            </div>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-secondary"
                                    disabled={page === 1}
                                    onClick={() => loadReport(page - 1)}
                                >
                                    Prev
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`btn btn-sm ${page === i + 1 ? "btn-primary" : "btn-outline-primary"}`}
                                        onClick={() => loadReport(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    className="btn btn-sm btn-secondary"
                                    disabled={page === totalPages}
                                    onClick={() => loadReport(page + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
